<template>
  <div v-if="ready">
    <div class="columns is-multiline is-vcentered has-less-padding is-centered">
      <div class="column is-4">
        <dropdown
          v-model="selectedYear"
          :options="years"
          value-key="description"
          text-key="description"
          default-option="Year"
          placeholder="Year"
        />
      </div>
      <div class="column is-2 has-text-right">
        <button
          id="search-btn"
          class="button is-secondary"
          :class="{ 'is-loading' : isDownloading }"
          type="submit"
          :disabled="isDownloading"
          @click.prevent="exportCsv"
        >
          <span>Download CSV</span>
          <span class="icon is-small">
            <i class="fad fa-file-download" />
          </span>
        </button>
      </div>
    </div>
    <h3 class="is-size-5">
      {{ isLoadingTable ? 'Loading results...' : `Results for ${selectedYear}` }}
    </h3>
    <v-client-table
      :loading="true"
      :columns="tableColumns"
      :options="tableOptions"
      :data="filingsByMonth.rows"
    >
      <tfoot slot="afterBody">
        <tr>
          <td><b> Totals: </b></td>
          <td><b>{{ filingsByMonth.totals.online }} ({{ inPercents(filingsByMonth.totals.online, filingsByMonth.totals.all) }}%)</b></td>
          <td><b>{{ filingsByMonth.totals.paper }} ({{ inPercents(filingsByMonth.totals.paper, filingsByMonth.totals.all) }}%)</b></td>
          <td><b>{{ filingsByMonth.totals.all }}</b></td>
        </tr>
      </tfoot>
    </v-client-table>
  </div>
</template>
<script>
import Vue from 'vue';
import { ClientTable } from 'vue-tables-2';
import { Dropdown } from '@phila/phila-ui';
import vSelectMenu from 'v-selectmenu';
import { controlValueHelpers } from '@/mixins/controlValue';
import { ExportToCsv } from 'export-to-csv';

Vue.use(vSelectMenu);
Vue.use(ClientTable, {}, false, 'bulma');

export default {
  name: 'FilingsByMonth',
  components: {
    Dropdown,
  },
  mixins: [
    controlValueHelpers,
  ],
  data () {
    return {
      isLoadingTable: false,
      ready: false,
      isDownloading: false,
      selectedYear: '',
      tableColumns: [
        "month",
        "online",
        "paper",
        "total",
      ],
      tableOptions: {
        columnsClasses: {},
        sortIcon: {},
        perPage: 1000,
        perPageValues: [],
        sortable: [],
        filterable: false,
        pagination: {
          edge: true,
          nav: 'scroll',
        },
      },
    };
  },
  computed: {
    filingsByMonth () {
      return this.$store.getters['reports/filingsByMonth'];
    },
    years () {
      let years = this.$store.getters['admin/activeControlValues']('filingyears');
      if (years) {
        return this.cloneObj(this.$store.getters['admin/activeControlValues']('filingyears')).sort((a, b) => {
          return b.sequenceNo - a.sequenceNo;
        });
      }
      return [];
    },
  },
  watch: {
    selectedYear () {
      this.isLoadingTable = true;
      this.getFilingsByYear();
    },
  },
  async created () {
    this.$store.commit('loading', true);
    await this.initialFetch();
    await this.initialAssign();
    this.$store.commit('loading', false);
  },
  methods: {
    inPercents (num, total) {
      return (num / total * 100).toFixed(2);
    },
    async exportCsv () {
      this.isDownloading = true;
      let exportData = [];
      this.filingsByMonth.rows.forEach(row => {
        exportData.push({
          month: row.month,
          online: row.online,
          paper: row.paper,
          total: row.total,
        });
      });
      exportData.push({
        month: 'Totals',
        online: `${this.filingsByMonth.totals.online} (${this.inPercents(this.filingsByMonth.totals.online, this.filingsByMonth.totals.all)}%)`,
        paper: `${this.filingsByMonth.totals.paper} (${this.inPercents(this.filingsByMonth.totals.paper, this.filingsByMonth.totals.all)}%)`,
        total: this.filingsByMonth.totals.all,
      });
      const csvExporter = new ExportToCsv({
        filename: `${this.selectedYear}-filings-by-month`,
        useKeysAsHeaders: false,
        showLabels: true,
        showTitle: false,
        headers: [ 'Month', 'Online', 'Paper', 'Total' ],
      });
      csvExporter.generateCsv(exportData);
      this.isDownloading = false;
    },
    async initialAssign () {
      this.selectedYear = this.years[0].description;
      await this.getFilingsByYear();
      this.ready = true;
    },
    async getFilingsByYear () {
      await this.$store.dispatch('reports/filingsByYear', this.selectedYear);
      this.isLoadingTable = false;
    },
    async initialFetch () {
      let promises = [];
      promises.push(this.$store.dispatch('admin/getControlValue', 'filingyears'));
      return Promise.all(promises);
    },
  },
};
</script>

<style lang="scss">
  .columns.has-less-padding {
    .column {
      padding: .4rem 0.75em ;
    }
  }
  .v-dropdown-caller {
    width: 100%;
    .sm-caller-container {
      width: 100%;
      button.sm-default-btn {
        width: 100%;
        max-width: 100%;
        text-align: left;
        border-radius: 0;
        box-shadow: none;
        height: 40px;
        padding: 0 0 0 10px;
        vertical-align: middle;
        > div {
          max-width: 90%;
          display: inline-block;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          line-height: 40px;
          &:before {
            content: " ";
            background: white;
            width: 30px;
            height: 100%;
            display: block;
            z-index: 9;
            top: 0;
            right: 0;
            position: absolute;
          }
          &:after {
            content: "";
            right: 1.125em;
            z-index: 10;
            border: 3px solid transparent;
            border-color: #0f4d8f;
            border-radius: 2px;
            border-right: 0;
            border-top: 0;
            display: block;
            height: 0.625em;
            margin-top: -0.4375em;
            pointer-events: none;
            position: absolute;
            top: 50%;
            transform-origin: center;
            width: 0.625em;
            transform: rotate(-45deg);
          }
        }
      }
    }
  }
</style>